import React, { useCallback, useEffect, useMemo, useState } from "react";

import { queryCache } from "react-query";

import {
  PROPERTY_QUERY_KEYS,
  useGetBillingProperties,
} from "manager/hooks/api";
import { Property } from "manager/interfaces/api";

import {
  AssignEmployeesToPropertyParams,
  AssignPropertyToPortfolioParams,
  CreateUnitParams,
  DefaultPagination,
  PropertiesSearchParams,
  UnassignPropertyFromPortfolioParams,
} from "../interfaces";

import {
  BILLING_SECTION_PAGE_SIZE,
  LIVE_SECTION_STATUSES,
  NOT_LIVE_SECTION_STATUSES,
  ONBOARDING_SECTION_STATUSES,
  billingListPage,
  SECTION_COPY,
} from "./billingDisplay";
import BillingSectionTable from "./BillingSectionTable";
import {
  InlineHint,
  InternalHint,
  SectionCount,
  SectionHeader,
  SectionTitle,
  SectionTitleRow,
  StaffBadge,
} from "./styled";
import { useBillingSectionActions } from "./useBillingSectionActions";

interface BillingPropertiesSectionsProps {
  propertiesSearchParams: PropertiesSearchParams;
  hasInputValue: boolean;
  isMagicPortalEnabled: boolean;
  showPrequalificationCheckbox: boolean;
  onTogglePropertyDisplayOnPortal: (propertyId: string | number) => void;
  onTogglePropertyPrequalification: (propertyId: string | number) => void;
  createUnit: (context: CreateUnitParams) => void;
  assignEmployeesToProperty: (context: AssignEmployeesToPropertyParams) => void;
  unassignPropertyFromPortfolio: (
    context: UnassignPropertyFromPortfolioParams
  ) => void;
  assignPropertyToPortfolio: (context: AssignPropertyToPortfolioParams) => void;
  isSavingDisplayOnPortal?: boolean;
  isSavingPrequalification?: boolean;
}

const useBillingSectionQuery = ({
  billingStatus,
  pageSize,
  searchText,
  portfolioKey,
}: {
  billingStatus: string;
  pageSize: number;
  searchText: string;
  portfolioKey: string;
}) => {
  const [pagination, setPagination] = useState<DefaultPagination>({
    page: 1,
    pageSize,
  });
  useEffect(() => {
    setPagination({ page: 1, pageSize });
  }, [searchText, portfolioKey, pageSize]);

  const params = useMemo(
    () => ({
      nameOrAddress: searchText || undefined,
      portfolioId: portfolioKey || undefined,
      billingStatus,
      page: pagination.page,
      pageSize: pagination.pageSize,
    }),
    [
      searchText,
      portfolioKey,
      billingStatus,
      pagination.page,
      pagination.pageSize,
    ]
  );

  const { data, isLoading } = useGetBillingProperties({ params });
  const { rows, total } = billingListPage(data);
  return {
    properties: rows as Property[],
    count: total,
    pagination,
    setPagination,
    isLoading,
  };
};

const BillingPropertiesSections = ({
  propertiesSearchParams,
  hasInputValue,
  isSavingDisplayOnPortal,
  isSavingPrequalification,
  ...tableProps
}: BillingPropertiesSectionsProps) => {
  const searchText =
    propertiesSearchParams.nameOrAddress ||
    propertiesSearchParams.nameOrEmail ||
    "";
  const portfolioKey = (propertiesSearchParams.portfolioId || []).join(",");

  const onboarding = useBillingSectionQuery({
    billingStatus: ONBOARDING_SECTION_STATUSES,
    pageSize: BILLING_SECTION_PAGE_SIZE.onboarding,
    searchText,
    portfolioKey,
  });
  const live = useBillingSectionQuery({
    billingStatus: LIVE_SECTION_STATUSES,
    pageSize: BILLING_SECTION_PAGE_SIZE.live,
    searchText,
    portfolioKey,
  });
  const notLive = useBillingSectionQuery({
    billingStatus: NOT_LIVE_SECTION_STATUSES,
    pageSize: BILLING_SECTION_PAGE_SIZE.notLive,
    searchText,
    portfolioKey,
  });

  const refetchAll = useCallback(() => {
    queryCache.invalidateQueries(PROPERTY_QUERY_KEYS.getBillingProperties());
  }, []);

  const actions = useBillingSectionActions(refetchAll);

  const sectionTable = (section: "onboarding" | "live" | "notLive", list) => {
    const copy = SECTION_COPY[section];
    return (
      <div>
        <SectionHeader>
          <SectionTitleRow>
            <SectionTitle>{copy.title}</SectionTitle>
            <SectionCount tone={section}>{list.count}</SectionCount>
            {copy.staffOnly && <StaffBadge>Vero staff only</StaffBadge>}
            {section !== "onboarding" && <InlineHint>{copy.hint}</InlineHint>}
          </SectionTitleRow>
          {section === "onboarding" && <InternalHint>{copy.hint}</InternalHint>}
        </SectionHeader>
        <BillingSectionTable
          {...tableProps}
          {...actions}
          section={section}
          properties={list.properties}
          propertiesCount={list.count}
          loading={
            list.isLoading ||
            isSavingDisplayOnPortal ||
            isSavingPrequalification
          }
          hasInputValue={hasInputValue}
          pagination={list.pagination}
          setPagination={list.setPagination}
          propertiesSearchParams={propertiesSearchParams}
        />
      </div>
    );
  };

  return (
    <>
      {sectionTable("onboarding", onboarding)}
      {sectionTable("live", live)}
      {sectionTable("notLive", notLive)}
    </>
  );
};

export default BillingPropertiesSections;
